<template>
  <div class="filter">
    <unlock-upsell
      v-if="!hasFilterAccess"
      title="Unlock onX Fish to use <strong>All Filters.</strong>"
      data-test="upsell"
      class="species-upsell"
      @upgrade="upgrade"
    />
    <ys-text type="title4" class="filter-heading content-item">
      Fish Species
    </ys-text>
    <filter-checkbox
      :filter-key="FILTER_KEYS[FIELDS.FISH_TYPES]"
      :include-all="false"
      :origin="filterOrigin"
      class="content-item checkbox-group species"
      data-test="species"
      @change="handleCheckboxChange"
    />
    <div
      v-for="toggle in toggleMap"
      :key="toggle.eventType"
      class="toggle-wrapper"
    >
      <div class="species-toggle">
        <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
        <label :for="toggle.eventType">
          <ys-text type="subtitle4" class="text-label">
            {{ toggle.title }}
          </ys-text>
        </label>
        <ys-switch
          v-if="hasFilterAccess"
          :id="toggle.eventType"
          :selected="toggle.model"
          :disabled="isDisabled(toggle.storageId)"
          :data-test="`toggle${toggle.eventType}`"
          @change="handleToggleChange(toggle)"
        />
        <ys-icon v-else icon="ysLock" data-test="lock" size="medium" />
      </div>
      <ys-text type="body2" class="text-label">
        {{ toggle.subtitle }}
      </ys-text>
    </div>
  </div>
</template>
<script setup lang="ts">
///// imports /////
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import '@onxmaps/yellowstone-ui/ys-text'
import '@onxmaps/yellowstone-ui/ys-icon'
import '@onxmaps/yellowstone-ui/ys-switch'
import '@onxmaps/yellowstone-ui/icons/ysLock'

import filterCheckbox from '@/components/filter-checkbox.vue'
import unlockUpsell from '@/components/shared/unlock-upsell.vue'

import { saveItem, getItem } from '@/plugins/storage'
import { FILTER_KEYS } from '@/services/filter-layer-service/constants'
import {
  hasCheckboxFilter,
  getCheckboxFilterDataSlug,
} from '@/services/filter-layer-service'
import marketingEventService from '@/services/marketing-event-service'
import { camera } from '@/services/map-service'
import { FishClaims } from '@/services/feature-access/fish/types'
import { useHasAccess } from '@/services/feature-access/composables'

import { goToPurchase } from '@/lib/purchase-utils'
import {
  EVENTS,
  PURCHASE_CTA_ORIGINS,
  FILTER_ORIGINS,
  DISCOVER_ORIGINS,
} from '@/lib/enums-marketing'
import { HUMAN_SKUS } from '@/lib/plans'
import { FIELDS, SLUGS } from '@/fish/lib/taxonomy-constants'
import { MODES } from '@/fish/lib/enums'

import { applyMapFilteringDisplay } from '@/fish/lib/filtering/filter-map-display'
import { Vertical } from '@/types/verticals'
import { FishMode } from '@/types/modes'

import routeNames from '@/fish/router/constants'

///// types /////
type PotentialType = boolean

interface Toggle {
  title: string
  subtitle: string
  model: PotentialType
  storageId: string
  eventType: string
  analyticsName: string
}

///// refs and variables /////
const route = useRoute()
const allowedKeeperSpecies = [SLUGS.BLUEGILL, SLUGS.WALLEYE, SLUGS.CRAPPIE]
const { hasAccess: hasFilterAccess } = useHasAccess(
  FishClaims.waterbody_filters
)

///// computed /////
const trophyPotential = computed<PotentialType>(() =>
  getItem('fish_freshwater_filter_trophy')
)
const abundancePotential = computed<PotentialType>(() =>
  getItem('fish_freshwater_filter_abundance')
)
const keeperPotential = computed<PotentialType>(() =>
  getItem('fish_freshwater_filter_keeper')
)
const stockingEffort = computed<PotentialType>(() =>
  getItem('fish_freshwater_filter_stocking_effort')
)

const toggleMap = computed<Toggle[]>(() => [
  {
    title: 'Trophy Potential Lakes',
    subtitle: 'Lakes where trophy species have been surveyed.',
    model: trophyPotential.value,
    storageId: 'fish_freshwater_filter_trophy',
    eventType: 'Trophy',
    analyticsName: 'Trophy',
  },
  {
    title: 'High Abundance Lakes',
    subtitle: 'Lakes with a high species abundance.',
    model: abundancePotential.value,
    storageId: 'fish_freshwater_filter_abundance',
    eventType: 'Abundance',
    analyticsName: 'Abundance',
  },
  {
    title: 'Keeper Potential Lakes',
    subtitle: 'Lakes with Keeper-sized Bluegill, Crappie, or Walleye.',
    model: keeperPotential.value,
    storageId: 'fish_freshwater_filter_keeper',
    eventType: 'Keeper',
    analyticsName: 'Keeper',
  },
  {
    title: 'High Stocking Effort',
    subtitle:
      'Lakes with high stocking activity for a species based on volume, frequency, and life stage.',
    model: stockingEffort.value,
    storageId: 'fish_freshwater_filter_stocking_effort',
    eventType: 'Stocking',
    analyticsName: 'High Stocking Effort',
  },
])

// Check if one of active checkboxes contains a designated keeper fish
const hasKeeperSpeciesEnabled = computed(() =>
  getCheckboxFilterDataSlug(
    Vertical.Fish,
    FishMode.Freshwater,
    FILTER_KEYS[FIELDS.FISH_TYPES]
  ).some((slug) => allowedKeeperSpecies.includes(slug))
)

const hasActiveCheckbox = computed(() => {
  if (hasCheckboxFilter(FILTER_KEYS[FIELDS.FISH_TYPES])) {
    return true
  }
  return false
})

const purchaseOrigin = computed(() => {
  return route.name === routeNames.WATERBODIES_FILTER
    ? PURCHASE_CTA_ORIGINS.FILTER_CARD
    : PURCHASE_CTA_ORIGINS.QUICK_FILTER
})

const filterOrigin = computed(() => {
  let origin = ''
  if (route.name === routeNames.WATERBODIES) {
    origin = FILTER_ORIGINS.DISCOVER
  } else if (route.name === routeNames.WATERBODIES_FILTER) {
    origin = DISCOVER_ORIGINS.MODE
  } else {
    origin = FILTER_ORIGINS.MAP
  }
  return origin
})

///// functions /////
const isDisabled = (storageId: string): boolean => {
  const keeper = 'fish_freshwater_filter_keeper'
  // Disable keeper toggle unless one of the keeper fish is active
  if (storageId === keeper) {
    return !hasActiveCheckbox.value || !hasKeeperSpeciesEnabled.value
  }
  // Otherwise disable toggles unless any species is active
  return !hasActiveCheckbox.value
}

const handleToggleChange = async (toggle: Toggle) => {
  const currState = getItem(toggle.storageId)
  const nextState = !currState
  saveItem(toggle.storageId, nextState)
  const onOffState = nextState ? 'on' : 'off'
  await applyMapFilteringDisplay()
  marketingEventService.publish(EVENTS.FILTER_CHANGED, {
    origin: filterOrigin.value,
    filter_type: toggle.analyticsName,
    filter_status: onOffState,
    filter_change: toggle.analyticsName,
    filters_applied: onOffState,
    min: null,
    max: null,
    map_mode: MODES.FRESHWATER,
    zoom_level: camera()?.location?.height.toFixed(1) ?? null,
  })
}

// Uncheck toggles if all species are unselected
const handleCheckboxChange = async () => {
  await applyMapFilteringDisplay()
  if (!hasCheckboxFilter(FILTER_KEYS[FIELDS.FISH_TYPES])) {
    saveItem('fish_freshwater_filter_trophy', false)
    saveItem('fish_freshwater_filter_keeper', false)
    saveItem('fish_freshwater_filter_abundance', false)
    saveItem('fish_freshwater_filter_stocking_effort', false)
  }
}

const upgrade = () => {
  const sku = HUMAN_SKUS.FISH_ELITE_YEAR
  goToPurchase(
    {
      origin: purchaseOrigin.value,
      detail: 'Upgrade',
    },
    `product=${sku}&membership=elite`
  )
}
</script>
<style lang="scss" scoped>
.species-upsell {
  margin-bottom: 0;
}

.content-item {
  &.checkbox-group {
    padding-bottom: var(--ys-spacing-200);
  }
  &.checkbox-group.species {
    :deep(.filter-options) {
      display: flex;
    }
  }
}

.filter {
  &:last-child {
    padding-bottom: var(--ys-spacing-300);
  }
  &__title {
    margin-bottom: var(--ys-spacing-50);
  }
}

.filter-heading {
  position: sticky;
  width: 100%;
  top: 0;
  padding-bottom: var(--ys-spacing-250);
  padding-top: var(--ys-spacing-300);
  background-color: var(--ys-color-background-primary);
  z-index: 1;
}

.toggle-wrapper {
  padding-bottom: var(--ys-spacing-150);
}

.species-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  .text-label {
    padding-right: 8px;
  }
  label {
    margin-bottom: 0;
  }
}
</style>
